import { useEffect, useState } from "react";
import { Spinner } from "@hybrid-iptv/ui";
import { nativeGuideAvailable, nativeGuideProgress, type GuideProgress } from "../api/nativeGuide.js";

/** How often to ask while a download runs, and while none does. */
const DOWNLOADING_POLL_MS = 1_000;
const IDLE_POLL_MS = 5_000;

const mb = (bytes: number) => Math.round(bytes / 1_048_576);

/**
 * "Updating the guide" while the TV downloads the provider's guide (owner,
 * 2026-10-21: "shouldn't the optimizing popup be showing with a guide
 * download"). A guide older than six hours is replaced in the background, which
 * takes the onn box about seven minutes, and before this only the guide itself
 * said so (epg/GuideLoadingStatus.tsx). It sits in the top bar beside the bell
 * (TopBar.tsx), so it covers nothing that takes focus; the guide has no top bar
 * and shows its own line instead. Gone when the download ends.
 */
export function GuideUpdateNotice() {
  const [progress, setProgress] = useState<GuideProgress | null>(null);
  useEffect(() => {
    if (!nativeGuideAvailable()) return;
    let stopped = false;
    let timer: number | undefined;
    const ask = () => {
      nativeGuideProgress()
        .then((next) => {
          if (stopped) return;
          setProgress(next);
          timer = window.setTimeout(ask, next?.downloading ? DOWNLOADING_POLL_MS : IDLE_POLL_MS);
        })
        .catch(() => {
          // An older APK without the progress call: no notice, as before.
          stopped = true;
        });
    };
    ask();
    return () => {
      stopped = true;
      window.clearTimeout(timer);
    };
  }, []);
  if (!progress?.downloading) return null;
  const known = progress.total > 0;
  return (
    <div className="work-chip" role="status" aria-live="polite" title="You can keep watching.">
      <Spinner />
      <span className="work-chip__text">
        <strong>Updating the guide</strong> {known ? `${mb(progress.bytes)} of ${mb(progress.total)} MB` : `${mb(progress.bytes)} MB`}
      </span>
    </div>
  );
}
